/**
 * FTUE orchestration store (PEL-515).
 *
 * Headless engine that decides WHICH step shows WHEN and remembers what's been
 * seen. Presentation-agnostic: it exposes the active step id + a timeout-driven
 * fallback flag; the renderer (PEL-516) consumes a resolved view via `useFtue`.
 *
 * Persistence is ACCOUNT-LEVEL / SERVER-SIDE (PEL-515 decision update): the
 * "seen" set is hydrated from the current user's `ftue_state.completedSteps`
 * (via the existing /me fetch) and writes go through a pluggable persist
 * adapter (optimistic cache update + fire-and-forget PATCH). The store itself
 * stays decoupled from react-query — `useFtueServerSync` wires the adapter and
 * calls `hydrate`. (The cosmetic `pelgo:coach_anim:{jobId}` localStorage flags
 * are unrelated and stay as-is.)
 *
 * Step lifecycle within a flow:
 *  - Evaluation is gated on `hydrated`: nothing shows until server state loads.
 *  - Evaluation picks the lowest-order unseen step whose `condition` passes.
 *  - Immediate steps activate at once; event-gated steps wait for `signalEvent`
 *    and arm a fallback timer so they can never hang (acceptance criterion).
 *  - `complete`/`dismiss` mark the step seen (optimistic) + persist, then advance.
 */

import { create } from 'zustand'
import { getFlowSteps } from './registry'
import type { FtueEventName, FtueStep } from './types'

/** Timers live outside store state (non-serializable, one per armed step). */
const timers = new Map<string, ReturnType<typeof setTimeout>>()

function clearTimer(stepId: string): void {
  const t = timers.get(stepId)
  if (t) {
    clearTimeout(t)
    timers.delete(stepId)
  }
}

function clearAllTimers(): void {
  for (const t of timers.values()) clearTimeout(t)
  timers.clear()
}

/**
 * Pluggable persistence adapter. `useFtueServerSync` registers one that does an
 * optimistic /me cache update + fire-and-forget PATCH. Default is a no-op so the
 * engine works (in-memory only) in tests or before sync mounts.
 */
export interface FtuePersistAdapter {
  markSeen: (stepIds: string[]) => void
}

let persistAdapter: FtuePersistAdapter | null = null

export function setFtuePersist(adapter: FtuePersistAdapter | null): void {
  persistAdapter = adapter
}

function persist(stepIds: string[]): void {
  if (stepIds.length > 0) persistAdapter?.markSeen(stepIds)
}

interface FtueState {
  activeFlow: string | null
  activeStepId: string | null
  /** Set when an event-gated step timed out and resolved to a fallback surface. */
  timedOut: boolean
  /** True once server onboarding state has been loaded; gates all activation. */
  hydrated: boolean
  seen: Set<string>
  signals: Set<FtueEventName>

  startFlow: (flowId: string) => void
  stopFlow: () => void
  /** Seed the seen-set from server state (union, monotonic). Sets hydrated. */
  hydrate: (stepIds: string[]) => void
  signalEvent: (name: FtueEventName) => void
  complete: (stepId: string) => void
  dismiss: (stepId: string) => void
  skipAll: (flowId: string) => void
  next: () => void
  back: () => void
}

export const useFtueStore = create<FtueState>((set, get) => {
  /** Lowest-order unseen step whose condition passes — the current candidate. */
  function pickCandidate(flowId: string, seen: Set<string>): FtueStep | null {
    for (const step of getFlowSteps(flowId)) {
      if (seen.has(step.id)) continue
      if (step.condition && !step.condition()) continue
      return step
    }
    return null
  }

  /** Recompute the active step from current seen/signals and arm timers. */
  function evaluate(): void {
    const { activeFlow, hydrated, seen, signals } = get()
    if (!activeFlow) return

    // Don't show anything until server onboarding state has hydrated.
    if (!hydrated) {
      set({ activeStepId: null, timedOut: false })
      return
    }

    const candidate = pickCandidate(activeFlow, seen)
    if (!candidate) {
      clearAllTimers()
      set({ activeStepId: null, timedOut: false })
      return
    }

    if (candidate.trigger.type === 'immediate') {
      clearAllTimers()
      set({ activeStepId: candidate.id, timedOut: false })
      return
    }

    // Event-gated: activate once its signal has fired.
    const eventName = candidate.trigger.event
    if (signals.has(eventName)) {
      clearTimer(candidate.id)
      set({ activeStepId: candidate.id, timedOut: false })
      return
    }

    // Armed and waiting — nothing visible yet. Arm a one-shot fallback timer so
    // a gated step never hangs if its event never arrives.
    set({ activeStepId: null, timedOut: false })
    if (candidate.fallback && !timers.has(candidate.id)) {
      timers.set(
        candidate.id,
        setTimeout(() => {
          timers.delete(candidate.id)
          const state = get()
          if (!state.activeFlow) return
          const stillCandidate = pickCandidate(state.activeFlow, state.seen)
          // Only fire the fallback if this step is still the waiting candidate
          // and its event genuinely never arrived.
          if (
            !stillCandidate ||
            stillCandidate.id !== candidate.id ||
            state.signals.has(eventName)
          ) {
            return
          }
          if (candidate.fallback?.resolveAs === 'skip') {
            get().complete(candidate.id)
          } else {
            set({ activeStepId: candidate.id, timedOut: true })
          }
        }, candidate.fallback.timeoutMs),
      )
    }
  }

  return {
    activeFlow: null,
    activeStepId: null,
    timedOut: false,
    hydrated: false,
    seen: new Set<string>(),
    signals: new Set<FtueEventName>(),

    startFlow: (flowId) => {
      set({ activeFlow: flowId })
      evaluate()
    },

    stopFlow: () => {
      clearAllTimers()
      set({ activeFlow: null, activeStepId: null, timedOut: false })
    },

    hydrate: (stepIds) => {
      set((s) => {
        const seen = new Set(s.seen)
        for (const id of stepIds) seen.add(id)
        return { seen, hydrated: true }
      })
      evaluate()
    },

    signalEvent: (name) => {
      set((s) => {
        if (s.signals.has(name)) return s
        return { signals: new Set(s.signals).add(name) }
      })
      evaluate()
    },

    complete: (stepId) => {
      clearTimer(stepId)
      set((s) => ({ seen: new Set(s.seen).add(stepId), activeStepId: null, timedOut: false }))
      persist([stepId])
      evaluate()
    },

    dismiss: (stepId) => {
      // Dismiss persists as seen too, so a dismissed step never re-fires (PEL-515 acceptance).
      get().complete(stepId)
    },

    skipAll: (flowId) => {
      // Scope: by default skipping exits the WHOLE flow (every step marked seen).
      // A step may opt into phase-scoped skip (`skipScope: 'phase'`) — used by a
      // state-branched flow where each phase is an independent mini-tour for a
      // different app state, so skipping one branch must NOT consume the others.
      // We derive the scope from the active step (the one whose coachmark fired the
      // skip), keeping the `skipAll(flowId)` signature unchanged for all callers.
      const steps = getFlowSteps(flowId)
      const active = steps.find((s) => s.id === get().activeStepId)
      const phase = active?.skipScope === 'phase' ? active.phase : undefined
      const target = phase ? steps.filter((s) => s.phase === phase) : steps
      const ids = target.map((s) => s.id)
      if (phase) {
        for (const id of ids) clearTimer(id)
      } else {
        clearAllTimers()
      }
      set((s) => {
        const seen = new Set(s.seen)
        for (const id of ids) seen.add(id)
        return { seen, activeStepId: null, timedOut: false }
      })
      persist(ids)
    },

    next: () => {
      const id = get().activeStepId
      if (id) get().complete(id)
    },

    back: () => {
      const { activeFlow, activeStepId } = get()
      if (!activeFlow || !activeStepId) return
      const steps = getFlowSteps(activeFlow)
      const idx = steps.findIndex((s) => s.id === activeStepId)
      if (idx <= 0) return
      const prev = steps[idx - 1]
      if (!prev) return
      // Re-show the previous step this session (local navigation only — we don't
      // un-persist server progress).
      set((s) => {
        const seen = new Set(s.seen)
        seen.delete(prev.id)
        return { activeStepId: prev.id, timedOut: false, seen }
      })
    },
  }
})
